import { useState, useEffect } from "react";
import { message } from "antd";

/**
 * Manages the hold queue state for call center calls.
 * Handles adding calls to hold, persisting to localStorage, and resuming held calls.
 *
 * @param {Object} params
 * @param {Array} params.holdQueue - Current hold queue from parent
 * @param {Function} params.setHoldQueue - Setter for hold queue in parent
 * @param {Function} params.setCart - Cart setter
 * @param {Object} params.clearCartData - Empty cart template
 * @param {Function} params.setCallCenterVisible - Visibility setter
 * @param {Function} params.setOrderHistoryDetails - Order history setter
 * @param {Function} params.setIsPaymentSelected - Payment selection setter
 * @param {Function} params.setSelectedStore - Store setter
 * @param {Function} params.setAhtSeconds - AHT seconds setter
 * @param {React.MutableRefObject} params.timerRef - Timer ref for interval management
 * @param {Function} params.formatAHT - AHT formatter
 * @returns {{ holdRecords: Array, isOnHold: boolean, setIsOnHold: Function, resumeHoldCall: Function, putCallOnHold: Function }}
 */
const useHoldQueue = ({
  holdQueue,
  setHoldQueue,
  setCart,
  clearCartData,
  setCallCenterVisible,
  setOrderHistoryDetails,
  setIsPaymentSelected,
  setSelectedStore,
  setAhtSeconds,
  timerRef,
  formatAHT,
}) => {
  const [holdRecords, setHoldRecords] = useState([]);
  const [isOnHold, setIsOnHold] = useState(false);

  useEffect(() => {
    localStorage.setItem("callCenterHoldQueue", JSON.stringify(holdQueue));
  }, [holdQueue]);

  // Resume a held call: restore cart, AHT, and open call screen
  const resumeHoldCall = (holdEntry) => {
    // Restore full cart
    setCart(holdEntry.cart);
    setSelectedStore(holdEntry.selectedStore);
    // Resume AHT timer from saved time
    setAhtSeconds(holdEntry.ahtSeconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setAhtSeconds((prev) => prev + 1);
    }, 1000);

    // Remove from hold queue
    setHoldQueue((prev) => prev.filter((c) => c.id !== holdEntry.id));

    // Show call screen
    setCallCenterVisible(true);
  };

  // Put current call on hold
  const putCallOnHold = ({ cart, selectedStore, ahtSeconds }) => {
    // Pause timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Save to holdQueue
    const customer = cart?.customer || {};
    const entry = {
      id: Date.now(),
      name: customer.name || "Guest",
      phone: customer.mobileNo ? `+91 ${customer.mobileNo}` : "+91 –",
      city: customer.customerAddress?.city || "–",
      aht: formatAHT(ahtSeconds),
      ahtSeconds: ahtSeconds,
      priority: "MEDIUM",
      timestamp: new Date().toLocaleString(),
      cart: cart,
      selectedStore: selectedStore,
    };

    setHoldQueue((prev) => [...prev, entry]);
    message.success(`Call on hold: ${entry.name} – ${entry.aht}`);
    setCart(clearCartData);
    // Navigate to call center page
    setCallCenterVisible(false);
    setOrderHistoryDetails([]);
    setIsPaymentSelected(false);
  };

  return {
    holdRecords,
    isOnHold,
    setIsOnHold,
    resumeHoldCall,
    putCallOnHold,
  };
};

export default useHoldQueue;
